import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../../services/firebase';
import { generateUniqueSlug } from '../../utils/slugUtils';
import './ProductForm.css';

const ProductForm = ({ product, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    fullDescription: product?.fullDescription || '',
    category: product?.category || 'fertilizers',
    benefits: product?.benefits || [],
    ecommerceLinks: product?.ecommerceLinks || [],
    isInStock: product?.isInStock ?? true,
    isFeatured: product?.isFeatured ?? false,
    featuredSequence: product?.featuredSequence || 0,
    rating: product?.rating || 0,
    reviews: product?.reviews || 0,
    image: product?.image || '',
    images: product?.images || []
  });

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [benefitText, setBenefitText] = useState('');
  const [ecommercePlatform, setEcommercePlatform] = useState('');
  const [ecommerceQuantity, setEcommerceQuantity] = useState('');
  const [ecommerceUrl, setEcommerceUrl] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const categories = [
    { id: 'fertilizers', name: 'Fertilizers' },
    { id: 'seeds', name: 'Seeds' },
    { id: 'plants', name: 'Plants' },
    { id: 'tools', name: 'Tools' },
    { id: 'soil-mix', name: 'Soil Mix' },
    { id: 'decoratives', name: 'Decoratives' },
    { id: 'other', name: 'Other' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (files) => {
    if (files.length === 0) return;
    
    setUploadingImages(true);
    const uploadedUrls = [];
    
    try {
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error('You must be logged in to upload images. Please log in and try again.');
      }
      
      console.log('Starting image upload...', { fileCount: files.length, user: auth.currentUser.email });
      
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`File "${file.name}" is not an image. Please select image files only.`);
        }
        
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        }
        
        const imageRef = ref(storage, `products/${Date.now()}_${file.name}`);
        console.log('Uploading file:', file.name, 'to:', imageRef.fullPath);
        
        await uploadBytes(imageRef, file);
        const url = await getDownloadURL(imageRef);
        console.log('Upload successful, URL:', url);
        uploadedUrls.push(url);
      }
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls],
        image: uploadedUrls[0] // Set first image as main image
      }));
      
      console.log('All images uploaded successfully');
    } catch (error) {
      console.error('Error uploading images:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Show detailed error message
      let errorMessage = 'Error uploading images. ';
      
      if (error.code === 'storage/unauthorized') {
        errorMessage += 'Permission denied. Please check Firebase Storage security rules and ensure you are logged in.';
      } else if (error.code === 'storage/canceled') {
        errorMessage += 'Upload was canceled.';
      } else if (error.code === 'storage/unknown') {
        errorMessage += 'Unknown error occurred. Please check your internet connection and try again.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please check the browser console for more details.';
      }
      
      alert(errorMessage);
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      // If removing the main image, set the first remaining image as main
      let newMainImage = prev.image;
      if (prev.images[index] === prev.image) {
        newMainImage = newImages.length > 0 ? newImages[0] : '';
      }
      return {
        ...prev,
        images: newImages,
        image: newMainImage
      };
    });
  };

  // Drag and drop handlers for image reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setFormData(prev => {
      const newImages = [...prev.images];
      const draggedImage = newImages[draggedIndex];
      
      // Remove dragged item from its original position
      newImages.splice(draggedIndex, 1);
      
      // Insert at new position
      newImages.splice(dropIndex, 0, draggedImage);
      
      // Always set first image as main image after reordering
      const newMainImage = newImages[0];
      
      return {
        ...prev,
        images: newImages,
        image: newMainImage
      };
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const setMainImage = (imageUrl) => {
    setFormData(prev => ({
      ...prev,
      image: imageUrl
    }));
  };


  const addBenefit = () => {
    if (benefitText.trim()) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, benefitText.trim()]
      }));
      setBenefitText('');
    }
  };

  const removeBenefit = (index) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const addEcommerceLink = () => {
    if (ecommercePlatform && ecommerceUrl) {
      setFormData(prev => ({
        ...prev,
        ecommerceLinks: [...prev.ecommerceLinks, {
          platform: ecommercePlatform,
          quantity: ecommerceQuantity || '', // Optional quantity field
          url: ecommerceUrl
        }]
      }));
      setEcommercePlatform('');
      setEcommerceQuantity('');
      setEcommerceUrl('');
    }
  };

  const removeEcommerceLink = (index) => {
    setFormData(prev => ({
      ...prev,
      ecommerceLinks: prev.ecommerceLinks.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || formData.name.trim() === '') {
      alert('Please enter a product name.');
      return;
    }
    
    if (!formData.image || formData.image === '') {
      alert('Please upload at least one product image.');
      return;
    }
    
    // Check authentication
    if (!auth.currentUser) {
      alert('You must be logged in to add products. Please log in and try again.');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('=== SUBMITTING PRODUCT ===');
      console.log('Form data:', formData);
      console.log('User:', auth.currentUser.email);
      
      // Generate unique slug for the product
      console.log('Generating slug...');
      const slug = await generateUniqueSlug(formData.name, 'products', product?.id);
      console.log('Generated slug:', slug);
      
      // Add slug to form data
      const dataWithSlug = {
        ...formData,
        slug: slug
      };
      
      console.log('Calling onSubmit with data:', dataWithSlug);
      await onSubmit(dataWithSlug);
      console.log('Product submitted successfully');
    } catch (error) {
      console.error('=== ERROR SUBMITTING PRODUCT ===');
      console.error('Error object:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      let errorMessage = 'Error submitting product. ';
      
      if (error.code === 'permission-denied') {
        errorMessage += 'Permission denied. Please check Firebase security rules and ensure you are logged in.';
      } else if (error.code === 'unavailable') {
        errorMessage += 'Firebase service is unavailable. Please check your internet connection and try again.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please check the browser console (F12) for more details.';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="product-form-container">
      <div className="form-header">
        <h2>{product ? 'Edit Product' : 'Add New Product'}</h2>
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="product-form">
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Product Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter product name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>


          <div className="form-group">
            <label htmlFor="fullDescription">Full Description</label>
            <textarea
              id="fullDescription"
              name="fullDescription"
              value={formData.fullDescription}
              onChange={handleInputChange}
              rows="5"
              placeholder="Detailed product description"
            />
          </div>
        </div>


        {/* Images */}
        <div className="form-section">
          <h3>Product Images</h3>
          
          <div className="image-upload">
            <input
              type="file"
              id="images"
              multiple
              accept="image/*"
              onChange={(e) => handleImageUpload(Array.from(e.target.files))}
              className="file-input"
            />
            <label htmlFor="images" className="file-label">
              {uploadingImages ? 'Uploading...' : 'Choose Images'}
            </label>
            <p className="upload-hint">You can select multiple images</p>
          </div>

          {formData.images.length > 0 && (
            <div className="image-preview">
              <p className="image-preview-label">
                Drag images to reorder. First image is the main display image.
              </p>
              {formData.images.map((url, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`preview-item ${url === formData.image ? 'main-image' : ''} ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                >
                  <div className="drag-handle" title="Drag to reorder">
                    ⋮⋮
                  </div>
                  <img src={url} alt={`Product ${index + 1}`} draggable={false} />
                  <div className="image-controls">
                    <label className="main-image-checkbox">
                      <input
                        type="radio"
                        name="mainImage"
                        checked={url === formData.image}
                        onChange={() => setMainImage(url)}
                      />
                      <span className="checkmark">Main Image</span>
                    </label>
                    <div className="image-index">{index + 1}</div>
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() => removeImage(index)}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>


        {/* Benefits */}
        <div className="form-section">
          <h3>Benefits</h3>
          
          <div className="benefit-input">
            <input
              type="text"
              placeholder="Enter benefit"
              value={benefitText}
              onChange={(e) => setBenefitText(e.target.value)}
            />
            <button type="button" onClick={addBenefit}>
              Add
            </button>
          </div>

          <div className="benefits-list">
            {formData.benefits.map((benefit, index) => (
              <div key={index} className="benefit-item">
                <span>{benefit}</span>
                <button
                  type="button"
                  onClick={() => removeBenefit(index)}
                  className="remove-benefit"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* E-commerce Links */}
        <div className="form-section">
          <h3>E-commerce Links</h3>
          
          <div className="ecommerce-input">
            <input
              type="text"
              placeholder="Platform (e.g., Amazon)"
              value={ecommercePlatform}
              onChange={(e) => setEcommercePlatform(e.target.value)}
            />
            <input
              type="text"
              placeholder="Quantity (e.g., 1Kg, 2Kg) - Optional"
              value={ecommerceQuantity}
              onChange={(e) => setEcommerceQuantity(e.target.value)}
            />
            <input
              type="url"
              placeholder="Product URL"
              value={ecommerceUrl}
              onChange={(e) => setEcommerceUrl(e.target.value)}
            />
            <button type="button" onClick={addEcommerceLink}>
              Add
            </button>
          </div>

          <div className="ecommerce-links">
            {formData.ecommerceLinks.map((link, index) => (
              <div key={index} className="ecommerce-item">
                <div className="link-info">
                  <span className="platform">{link.platform}</span>
                  {link.quantity && <span className="quantity">({link.quantity})</span>}
                </div>
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="link-url">
                  View Link
                </a>
                <button
                  type="button"
                  onClick={() => removeEcommerceLink(index)}
                  className="remove-link"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Information */}
        <div className="form-section">
          <h3>Additional Information</h3>
          

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="rating">Rating</label>
              <input
                type="number"
                id="rating"
                name="rating"
                value={formData.rating}
                onChange={handleInputChange}
                min="0"
                max="5"
                step="0.1"
                placeholder="4.5"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="reviews">Number of Reviews</label>
              <input
                type="number"
                id="reviews"
                name="reviews"
                value={formData.reviews}
                onChange={handleInputChange}
                min="0"
                placeholder="128"
              />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isInStock"
                checked={formData.isInStock}
                onChange={handleInputChange}
              />
              Product is in stock
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleInputChange}
              />
              Featured Product (appears in home page featured section)
            </label>
          </div>

          {formData.isFeatured && (
            <div className="form-group">
              <label htmlFor="featuredSequence">Featured Sequence (Order)</label>
              <input
                type="number"
                id="featuredSequence"
                name="featuredSequence"
                value={formData.featuredSequence}
                onChange={handleInputChange}
                min="0"
                placeholder="0"
                className="sequence-input"
              />
              <p className="field-hint">
                Lower numbers appear first. Use 0, 1, 2, 3... to control the order of featured products.
              </p>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading || uploadingImages}
          >
            {loading ? 'Saving...' : (product ? 'Update Product' : 'Add Product')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
